import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import { Avatar, ListItem, ListItemAvatar, ListItemButton, ListItemText, Stack, Typography } from "@mui/material";

import { CaseNoteResponse } from "@/api";
import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { HighlightedText } from "@/common/components/highlighted-text/HighlightedText";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { useCaseNoteLabel } from "@/common/hooks/useCaseNoteLabel";
import { formatUserCode } from "@/common/utils/formatUserCode";
import { formatCaseNoteDateTime } from "@/pages/clients/client/components/tabs/notes-tab/CaseNotes.utils";
import { AppointmentStatusChip } from "@/pages/clients/client/components/tabs/notes-tab/components/appointment-status-chip/AppointmentStatusChip";
import { useVisibleAppointmentStatus } from "@/pages/clients/client/components/tabs/notes-tab/hooks/useVisibleAppointmentStatus";
import { useTranslation } from "@/translations";

const AVATAR_LABEL_MAX_LENGTH = 3;

interface Props {
  caseNote: CaseNoteResponse;
  selected: boolean;
  onSelect: (selectedNote: CaseNoteResponse) => void;
}

export function CaseNoteListItem({ caseNote, selected, onSelect }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const caseNoteLabel = useCaseNoteLabel(caseNote);
  const attachedDocument = caseNote.attachedDocument;
  const highlightTerm = caseNote.searchHighlightTerm ?? "";
  const authorInitials = formatUserCode(caseNote.createdBy, t).slice(0, AVATAR_LABEL_MAX_LENGTH);
  const dateTime = formatCaseNoteDateTime(language, caseNote.date, caseNote.time);
  const appointmentStatus = useVisibleAppointmentStatus(caseNote);

  return (
    <ListItem disablePadding divider key={caseNote.caseNoteId}>
      <ListItemButton
        selected={selected}
        disableRipple
        sx={{
          alignItems: "center",
          bgcolor: selected ? (theme) => theme.palette.fsw["blue-5"] : undefined,
        }}
        onClick={() => onSelect(caseNote)}
      >
        <ListItemAvatar>
          <Avatar alt={authorInitials}>{authorInitials}</Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <EllipsisText value={caseNoteLabel} variant="body1" fontWeight={selected ? "bold" : "regular"}>
              {attachedDocument ? (
                <HighlightedText text={attachedDocument.fileName} searchTerm={highlightTerm} />
              ) : undefined}
            </EllipsisText>
          }
          secondary={
            <Typography variant="body2" color="text.secondary">
              {dateTime}
            </Typography>
          }
        />
        {appointmentStatus && (
          <Stack sx={{ ml: 1, flexShrink: 0 }}>
            <AppointmentStatusChip status={appointmentStatus} />
          </Stack>
        )}
        {attachedDocument && (
          <Stack sx={{ color: "action.active", ml: 1 }}>
            <DescriptionRounded />
          </Stack>
        )}
      </ListItemButton>
    </ListItem>
  );
}
